import { useAtomValue } from "@effect/atom-react";
import {
  EnvironmentRegistry,
  type PreparedConnection,
} from "@afar-research/client-runtime/connection";
import { createEnvironmentSessionAtoms } from "@afar-research/client-runtime/state/session";
import type { EnvironmentId } from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import { Atom, AsyncResult, AtomRegistry } from "effect/unstable/reactivity";

import { connectionAtomRuntime } from "../connection/runtime";
import { appAtomRegistry } from "../rpc/atomRegistry";

export const environmentSession = createEnvironmentSessionAtoms(connectionAtomRuntime);

const EMPTY_PREPARED_CONNECTION_ATOM = Atom.make(Option.none()).pipe(
  Atom.withLabel("web-prepared-connection:empty"),
);

export function usePreparedConnection(environmentId: EnvironmentId | null) {
  return useAtomValue(
    environmentId === null
      ? EMPTY_PREPARED_CONNECTION_ATOM
      : environmentSession.preparedConnectionValueAtom(environmentId),
  );
}

/**
 * The Environment's prepared connection right now, for code outside a rendered view. It reads
 * the connection registry itself rather than a session atom, because an atom no view subscribes
 * to starts empty on every read.
 */
export function readPreparedConnection(environmentId: EnvironmentId): PreparedConnection | null {
  const runtime = appAtomRegistry.get(connectionAtomRuntime);
  if (!AsyncResult.isSuccess(runtime)) return null;
  return Option.getOrNull(
    Context.get(runtime.value, EnvironmentRegistry).preparedConnection(environmentId),
  );
}

/** The Environment's prepared connection once it has one; aborting the signal stops waiting. */
export function awaitPreparedConnection(
  environmentId: EnvironmentId,
  signal: AbortSignal,
): Promise<PreparedConnection> {
  return Effect.runPromise(
    AtomRegistry.getResult(appAtomRegistry, connectionAtomRuntime).pipe(
      Effect.flatMap((context) =>
        Context.get(context, EnvironmentRegistry).awaitPreparedConnection(environmentId),
      ),
    ),
    { signal },
  );
}
